import { CardContent, DialogTitle, Grid, InputLabel, Stack, TextField, Divider, Button } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { Form, FormikProvider, useFormik } from 'formik';
import * as yup from 'yup';
import { NewConfigDialogFormValues, NewConfigDialogProps } from './dto';
import { LoadingButton } from '@mui/lab';
import { DataSourceConfig } from '../data-source-config';
import { DataSourceSchema } from '../data-source-config/schema';
import { useCallback, useEffect } from 'react';

const NewConfigDialogSchema = yup.object({
    name: yup.string().required('Table name is required'),
    dataSource: DataSourceSchema.required('Data source need to be defined'),
});

export const NewConfigDialog = ({ onCancel, onSubmit }: NewConfigDialogProps) => {
    const formik = useFormik<NewConfigDialogFormValues>({
        initialValues: {
            name: '',
            dataSource: null,
        },
        onSubmit: async (values) => {
            await onSubmit?.(values);
        },
        validationSchema: NewConfigDialogSchema,
    });

    const {
        handleSubmit,
        touched,
        errors,
        isSubmitting,
        getFieldProps,
        setFieldValue,
        values,
        isValid,
        dirty,
        setFieldTouched,
    } = formik;

    const handleUnload = useCallback(
        (e: BeforeUnloadEvent) => {
            e.preventDefault();
            if (isSubmitting || dirty) return;
        },
        [isSubmitting, dirty],
    );

    useEffect(() => {
        window.addEventListener('beforeunload', handleUnload);
        return () => {
            window.removeEventListener('beforeunload', handleUnload);
        };
    }, [handleUnload]);

    return (
        <FormikProvider value={formik}>
            <Form onSubmit={handleSubmit}>
                <DialogTitle>
                    <FormattedMessage id="inside-fifa.generic-tables.create-new-table" />
                </DialogTitle>
                <CardContent>
                    <Grid container spacing={3}>
                        <Grid item xs={12} rowGap={3}>
                            <Stack spacing={1}>
                                <InputLabel>
                                    <FormattedMessage id="inside-fifa.generic-tables.table-name" />
                                </InputLabel>
                                <TextField
                                    fullWidth
                                    id="name"
                                    error={touched.name && Boolean(errors.name)}
                                    helperText={touched.name && errors.name}
                                    disabled={isSubmitting}
                                    {...getFieldProps('name')}
                                />
                            </Stack>
                        </Grid>
                        <DataSourceConfig
                            value={values.dataSource}
                            onChange={(newDataSource) => {
                                setFieldValue('dataSource', newDataSource);
                            }}
                            onTouched={() => {
                                setFieldTouched('dataSource', true);
                            }}
                            disabled={isSubmitting}
                            error={errors.dataSource}
                            touched={touched.dataSource}
                        />
                    </Grid>
                </CardContent>
                <Divider />
                <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ px: 2.5, py: 2 }}>
                    <Button color="error" size="small" disabled={isSubmitting} onClick={onCancel}>
                        <FormattedMessage id="inside-fifa.cancel" />
                    </Button>
                    <LoadingButton
                        type="submit"
                        variant="contained"
                        size="small"
                        loading={isSubmitting}
                        disabled={!isValid || !dirty}
                    >
                        <FormattedMessage id="inside-fifa.generic-tables.create" />
                    </LoadingButton>
                </Stack>
            </Form>
        </FormikProvider>
    );
};
